/**
 * ============================================================
 * 数据导入页面 (ImportData) - v28
 * 基于 Excel 模板设计，48个字段完整匹配
 * 特殊匹配规则：
 *   - "未成年"+"扣除" → minor_deduction
 *   - "优质"+"奖励" → quality_reward
 *   - 月份前缀自动去除
 *   - .1 后缀处理
 * 去重覆盖：settlement_month + anchor_name
 * ============================================================
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { importApi, settlementApi, fieldDefinitionApi, clearDataApi } from '../api';
import { Upload, FileSpreadsheet, Trash2, X, Loader, CheckCircle, AlertTriangle, AlertCircle, Zap } from 'lucide-react';

interface PreviewRow { [key: string]: any }
interface TableInfo { name: string; label: string; description: string; warning: string }

// ========== 表头字段映射 ==========
// 规则：
//   - 多数字段：精确匹配（去除月份前缀后完全相等）
//   - 特殊字段：多关键词同时满足（未成年+扣除，优质+奖励）
//   - 未匹配的字段：自动忽略
const EXACT_MAP: Record<string, string> = {
  '结算月份': 'settlement_month',
  '主播昵称': 'anchor_name',
  '主播uid': 'anchor_uid',
  '主播房间号': 'anchor_room_id',
  '入会时间': 'join_date',
  '首次开播时间': 'first_stream_date',
  '经纪人昵称': 'broker_name',
  '经纪人uid': 'broker_uid',
  '招募经纪人昵称': 'recruit_broker_name',
  '招募经纪人uid': 'recruit_broker_uid',
  '主播支付': 'anchor_payment',
  '主播总激励': 'anchor_total_incentive',
  '主播非自提礼物收益': 'anchor_non_self_gift_income',
  '非自提合计底薪': 'non_self_base_salary',
  '合计活动收益': 'total_activity_income',
  '非自提运营奖励': 'non_self_operation_reward',
  '非自提运营惩罚': 'non_self_operation_penalty',
  '主播负向治理收益总扣除': 'governance_deduction',
  '主播负向治理收益总补回': 'governance_recovery',
  '开播时长(含连麦时长)': 'stream_duration',
  '有效开播天': 'valid_stream_days',
  '有效开播时长': 'valid_stream_hours',
  '是否入会无首播主播': 'is_no_first_stream',
  '是否TOP主播': 'is_top_anchor',
  'TOPstar主播评级': 'topstar_level',
  '总流水': 'total_stream',
  '是否是政策新主播': 'is_policy_new_anchor',
  '新主播任务流水': 'new_anchor_task_stream',
  '新主播激励分成': 'new_anchor_incentive_share',
  '新主播激励': 'new_anchor_incentive',
  '新主播结算周期': 'new_anchor_settlement_period',
  '直播有效天/新主播周期': 'valid_days_in_period',
  '直播有效时长/新主播周期': 'valid_hours_in_period',
  '当月新主播流水': 'current_month_new_anchor_stream',
  '次月待结算新主播流水': 'next_month_pending_new_anchor_stream',
  '老主播任务流水': 'old_anchor_task_stream',
  '老主播任务分成': 'old_anchor_task_share',
  '老主播任务激励': 'old_anchor_task_incentive',
  'TOP主播任务分成': 'top_anchor_task_share',
  'TOP主播任务激励': 'top_anchor_task_incentive',
  '成长任务分成': 'growth_task_share',
  '成长任务激励': 'growth_task_incentive',
  '流水任务分成': 'stream_task_share',
  '流水任务激励': 'stream_task_incentive',
};

/** 多关键词匹配规则 */
const MULTI_KEYWORD_RULES: { keywords: string[]; dbField: string }[] = [
  { keywords: ['未成年', '扣除'], dbField: 'minor_deduction' },
  { keywords: ['优质', '奖励'], dbField: 'quality_reward' },
];

/** 去除月份前缀 "4月xxx" → "xxx" */
function stripMonthPrefix(label: string): string {
  return label.replace(/^(\d{1,2})月/, '').trim();
}

/** 字段匹配
 *  1. 精确匹配（去除月份前缀后在 EXACT_MAP 中查找）
 *  2. 多关键词规则匹配（未成年+扣除，优质+奖励）
 *  3. 未匹配 → 忽略
 */
function matchField(excelHeader: string): string | null {
  const cleanLabel = stripMonthPrefix(excelHeader);

  // 1. 精确匹配
  if (EXACT_MAP[cleanLabel]) return EXACT_MAP[cleanLabel];
  if (EXACT_MAP[excelHeader]) return EXACT_MAP[excelHeader];

  // 2. 多关键词规则（仅特殊字段）
  for (const rule of MULTI_KEYWORD_RULES) {
    const allMatch = rule.keywords.every(kw =>
      cleanLabel.includes(kw) || excelHeader.includes(kw)
    );
    if (allMatch) return rule.dbField;
  }

  // 3. 未匹配 → 忽略
  return null;
}

/** 获取数据类型 */
function getDataType(label: string): 'string' | 'number' | 'boolean' | 'date' {
  if (label.startsWith('是否')) return 'boolean';
  if (label.includes('时间') || label.includes('日期') || label.includes('月份')) return 'date';
  if (label.includes('uid') || label.includes('房间号') || label.includes('评级') || label.includes('时长') || label.includes('天') || label.includes('周期')) return 'string';
  return 'number';
}

/** 解析 Excel */
async function parseExcel(file: File): Promise<{ data: PreviewRow[]; columns: string[] }> {
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[][];
  if (rows.length < 2) return { data: [], columns: [] };

  const headers = rows[0].map(h => String(h).trim());
  const dataRows = rows.slice(1).filter(r => r.some(c => c !== '' && c !== null));

  const data = dataRows.map(row => {
    const obj: PreviewRow = {};
    headers.forEach((h, i) => { if (row[i] !== undefined) obj[h] = row[i]; });
    return obj;
  });

  return { data, columns: headers };
}

export default function ImportData() {
  const [showPreview, setShowPreview] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [allData, setAllData] = useState<PreviewRow[]>([]);
  const [previewColumns, setPreviewColumns] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<{ inserted: number; updated: number; failed: number } | null>(null);
  const [matchedFields, setMatchedFields] = useState<{ excel: string; db: string }[]>([]);
  const [importHistory, setImportHistory] = useState<any[]>([]);
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [showClearSection, setShowClearSection] = useState(false);
  const [clearingTable, setClearingTable] = useState<string | null>(null);
  const [clearingAll, setClearingAll] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showSuccess = useCallback((m: string) => { setToast({ message: m, type: 'success' }); setTimeout(() => setToast(null), 3000); }, []);
  const showError = useCallback((m: string) => { setToast({ message: m, type: 'error' }); setTimeout(() => setToast(null), 5000); }, []);

  useEffect(() => { loadHistory(); loadTables(); }, []);
  const loadHistory = async () => { try { const r = await importApi.list(); setImportHistory(r); } catch {} };
  const loadTables = async () => { try { const r = await clearDataApi.getTables(); setTables(r); } catch {} };

  const handleFile = async (file: File) => {
    if (!file) return;
    setProcessing(true); setSelectedFile(file);
    try {
      const result = await parseExcel(file);
      if (result.data.length === 0) { showError('无数据行'); setProcessing(false); return; }

      // 匹配字段
      const matched: { excel: string; db: string }[] = [];
      const unmatched: string[] = [];
      result.columns.forEach(col => {
        const dbField = matchField(col);
        if (dbField) matched.push({ excel: col, db: dbField });
        else unmatched.push(col);
      });

      setMatchedFields(matched);
      setAllData(result.data);
      setPreviewData(result.data.slice(0, 100));
      setPreviewColumns(result.columns);
      setShowPreview(true);
      setImportResult(null);
      showSuccess(`解析成功: ${result.columns.length}列 ${result.data.length}行（匹配${matched.length}个字段，忽略${unmatched.length}个）`);
      console.log('[ImportData] 匹配字段:', matched);
      console.log('[ImportData] 未匹配字段:', unmatched);
    } catch { showError('文件解析失败'); }
    finally { setProcessing(false); }
  };

  const handleUpload = async () => {
    if (!selectedFile || matchedFields.length === 0 || allData.length === 0) { showError('无数据可导入'); return; }
    setProcessing(true);
    try {
      // 1. 创建导入记录
      const record = await importApi.create({
        fileName: selectedFile.name, fileSize: selectedFile.size,
        rowCount: allData.length, columnCount: matchedFields.length, headers: previewColumns
      });
      const importId = (record as any)?.id || Date.now();

      // 2. 保存字段定义
      const fields = matchedFields.map(({ excel, db }) => ({
        importId, fieldKey: db, fieldLabel: excel,
        category: '结算数据', dataType: getDataType(excel)
      }));
      await fieldDefinitionApi.createBatch(fields);

      // 3. 转换数据行 → 数据库字段格式
      const dbRows = allData.map(row => {
        const dbRow: Record<string, any> = { importId };
        for (const { excel, db } of matchedFields) {
          const val = row[excel];
          if (val === undefined || val === null || val === '') continue;
          dbRow[db] = val;
        }
        return dbRow;
      });

      // 4. 批量导入（后端处理去重覆盖）
      console.log('[ImportData] 准备导入', dbRows.length, '行');
      let totalInserted = 0, totalUpdated = 0, totalFailed = 0;
      for (let i = 0; i < dbRows.length; i += 50) {
        const batch = dbRows.slice(i, i + 50);
        const result = await settlementApi.importBatch(batch);
        totalInserted += result.count;
        if (result.updated) totalUpdated += result.updated;
        if (result.failed) totalFailed += result.failed;
      }

      setImportResult({ inserted: totalInserted, updated: totalUpdated, failed: totalFailed });

      if (totalInserted + totalUpdated === 0) {
        showError('导入失败：没有数据写入');
      } else {
        showSuccess(`导入完成！共${allData.length}行，新增${totalInserted}行，更新${totalUpdated}行`);
      }

      setShowPreview(false); setSelectedFile(null); setPreviewData([]); setAllData([]); setPreviewColumns([]); setMatchedFields([]);
      await loadHistory();
    } catch (err: any) { showError(err.message || '导入失败'); console.error(err); }
    finally { setProcessing(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确定删除？')) return;
    try { await importApi.delete(id); showSuccess('删除成功'); await loadHistory(); } catch { showError('删除失败'); }
  };

  const handleClearTable = async (table: TableInfo) => {
    if (!confirm(`确定清空"${table.label}"？\n\n警告：${table.warning}`)) return;
    
    setClearingTable(table.name);
    try {
      await clearDataApi.clearTable(table.name);
      showSuccess(`已清空 "${table.label}"`);
      if (table.name === 'import_records') {
        await loadHistory();
      }
    } catch (err: any) {
      showError(err.message || '清空失败');
    } finally {
      setClearingTable(null);
    }
  };

  const handleClearAll = async () => {
    if (!confirm('⚠️ 危险操作！\n\n确定清空所有业务数据？此操作将删除：\n- 结算数据\n- 导入记录\n- 字段定义\n- 账单数据\n- 仪表板统计\n- 月度趋势\n- 待办任务\n\n此操作不可撤销！')) return;
    
    setClearingAll(true);
    try {
      await clearDataApi.clearAll();
      showSuccess('已清空所有业务数据');
      await loadHistory();
    } catch (err: any) {
      showError(err.message || '清空失败');
    } finally {
      setClearingAll(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold flex items-center gap-2"><Upload className="w-6 h-6 text-[#3B82F6]" />数据导入</h1>

      {/* 上传区域 */}
      <div className="bg-[#1E293B] border border-dashed border-[#334155] rounded-xl p-8 text-center hover:border-[#3B82F6] transition-all cursor-pointer" onClick={() => fileRef.current?.click()}>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.currentTarget.value = ''; }} />
        <FileSpreadsheet className="w-10 h-10 text-[#64748B] mx-auto mb-3" />
        <p className="text-sm text-[#94A3B8]">点击或拖拽上传 Excel 文件</p>
        <p className="text-xs text-[#64748B] mt-1">支持 .xlsx, .xls, .csv</p>
        {selectedFile && <p className="text-xs text-[#3B82F6] mt-2">已选择: {selectedFile.name} ({(selectedFile.size/1024).toFixed(1)}KB)</p>}
      </div>

      {/* 字段匹配结果 */}
      {matchedFields.length > 0 && (
        <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-4">
          <h3 className="text-sm font-medium mb-2 flex items-center gap-1"><CheckCircle className="w-4 h-4 text-emerald-400" />字段匹配结果（共{matchedFields.length}个）</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1 text-xs max-h-48 overflow-auto">
            {matchedFields.map(({ excel, db }) => (
              <div key={excel} className="flex items-center gap-1 px-2 py-1 bg-[#0F172A] rounded">
                <span className="text-[#94A3B8] truncate" title={excel}>{excel}</span>
                <span className="text-[#64748B]">→</span>
                <span className="text-[#3B82F6] font-mono truncate">{db}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 预览 */}
      {showPreview && (
        <div className="bg-[#1E293B] border border-[#334155] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#334155] flex items-center justify-between">
            <h2 className="text-sm font-medium">数据预览（前{previewData.length}/{allData.length}行，{matchedFields.length}个匹配字段）</h2>
            <div className="flex gap-2">
              <button onClick={() => { setShowPreview(false); setSelectedFile(null); }} className="px-3 py-1.5 text-xs border border-[#334155] rounded hover:bg-[#334555]/30">取消</button>
              <button onClick={handleUpload} disabled={processing} className="px-4 py-1.5 text-xs bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded disabled:opacity-50 flex items-center gap-1">
                {processing ? <Loader className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                导入数据库
              </button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-xs">
              <thead className="bg-[#334155]/50 sticky top-0">
                <tr>{previewColumns.map(c => { const m = matchedFields.find(f => f.excel === c); return <th key={c} className={`px-3 py-2 text-left whitespace-nowrap ${m ? 'text-[#3B82F6]' : 'text-[#64748B]'}`}>{c}{m && <span className="text-[10px] text-emerald-400 ml-1">✓</span>}</th>; })}</tr>
              </thead>
              <tbody className="divide-y divide-[#334155]/50">
                {previewData.map((row, i) => <tr key={i} className="hover:bg-[#334155]/20">{previewColumns.map(c => <td key={c} className="px-3 py-1.5 whitespace-nowrap text-[#94A3B8]">{String(row[c] ?? '').substring(0, 30)}</td>)}</tr>)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 导入结果 */}
      {importResult && (
        <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-5">
          <h3 className="text-sm font-medium mb-3">导入统计</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-emerald-500/10 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-emerald-400">{importResult.inserted}</p><p className="text-xs text-[#94A3B8]">新增</p></div>
            <div className="bg-blue-500/10 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-blue-400">{importResult.updated}</p><p className="text-xs text-[#94A3B8]">更新（覆盖）</p></div>
            <div className="bg-red-500/10 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-red-400">{importResult.failed}</p><p className="text-xs text-[#94A3B8]">失败</p></div>
          </div>
        </div>
      )}

      {/* 数据清空区域 */}
      <div className="bg-[#1E293B] border border-[#334155] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#334155] flex items-center justify-between">
          <h2 className="text-sm font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            数据清空
          </h2>
          <button 
            onClick={() => setShowClearSection(!showClearSection)}
            className="text-xs text-[#94A3B8] hover:text-[#3B82F6] transition-colors"
          >
            {showClearSection ? '收起' : '展开'}
          </button>
        </div>
        
        {showClearSection && (
          <div className="p-5">
            {/* 单个表清空 */}
            <div className="space-y-3 mb-6">
              <p className="text-xs text-[#94A3B8] mb-3">选择要清空的数据表：</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {tables.map(table => (
                  <div 
                    key={table.name}
                    className="bg-[#0F172A] border border-[#334155] rounded-lg p-4 hover:border-[#3B82F6]/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-sm font-medium text-white">{table.label}</h3>
                        <p className="text-xs text-[#64748B] mt-0.5">{table.description}</p>
                      </div>
                      <button
                        onClick={() => handleClearTable(table)}
                        disabled={clearingTable === table.name || clearingAll}
                        className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                        title={`清空 ${table.label}`}
                      >
                        {clearingTable === table.name ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <div className="flex items-start gap-1.5 text-xs text-amber-400/80">
                      <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>{table.warning}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 清空所有数据 */}
            <div className="border-t border-[#334155] pt-4">
              <button
                onClick={handleClearAll}
                disabled={clearingAll || clearingTable !== null}
                className="w-full py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg border border-red-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {clearingAll ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    清空所有数据中...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    清空所有业务数据（保留系统配置）
                  </>
                )}
              </button>
              <p className="text-xs text-[#64748B] text-center mt-2">
                此操作将删除所有结算数据、账单、导入记录等业务数据，但保留用户、角色、公式等系统配置
              </p>
            </div>
          </div>
        )}
      </div>

      {/* 导入历史 */}
      <div className="bg-[#1E293B] border border-[#334155] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#334155]"><h2 className="text-sm font-medium">导入历史</h2></div>
        {importHistory.length === 0 ? (
          <div className="p-8 text-center text-[#64748B] text-sm">暂无导入记录</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#334155]/50"><tr><th className="px-4 py-3 text-left">文件名</th><th className="px-4 py-3 text-right">大小</th><th className="px-4 py-3 text-right">行数</th><th className="px-4 py-3 text-left">状态</th><th className="px-4 py-3 text-left">时间</th><th className="px-4 py-3 text-right">操作</th></tr></thead>
              <tbody className="divide-y divide-[#334155]/50">
                {importHistory.map(item => (
                  <tr key={item.id} className="hover:bg-[#334155]/20">
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-[#3B82F6]" /><span className="truncate max-w-[200px]">{item.fileName || item.file_name || '未知'}</span></div></td>
                    <td className="px-4 py-3 text-right text-[#94A3B8]">{item.fileSize ? (Number(item.fileSize)/1024).toFixed(1)+'KB' : '-'}</td>
                    <td className="px-4 py-3 text-right">{(item.rowCount ?? item.row_count ?? 0).toLocaleString()}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${item.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' : item.status === 'failed' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'}`}>{item.status}</span></td>
                    <td className="px-4 py-3 text-[#94A3B8]">{item.createdAt ? new Date(item.createdAt).toLocaleString('zh-CN') : item.created_at ? new Date(item.created_at).toLocaleString('zh-CN') : '-'}</td>
                    <td className="px-4 py-3 text-right"><button onClick={() => handleDelete(item.id)} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {toast && <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg text-sm font-medium shadow-lg z-50 ${toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'} text-white`}>{toast.message}</div>}
    </div>
  );
}
